/**
 * Direction helpers for the antd props that have no logical form.
 *
 * antd mirrors a lot under ConfigProvider direction="rtl" — the tree, tables,
 * form layout, dropdown alignment — but Drawer `placement` is physical and
 * stays put. A drawer that slides in from the right in English kept sliding in
 * from the right in Hebrew, which reads as the panel opening on the wrong
 * side: in RTL the inline-end edge is the LEFT one.
 */
export type Direction = 'ltr' | 'rtl';

/** Where a panel anchored to the inline-END edge should sit. */
export function endPlacement(direction: Direction): 'left' | 'right' {
  return direction === 'rtl' ? 'left' : 'right';
}


/**
 * antd placement tokens are PHYSICAL and stay put under `direction="rtl"`.
 *
 * ConfigProvider mirrors a lot — the tree, tables, form layout, a dropdown
 * that was never given an explicit placement — but the moment a component
 * carries `placement="bottomLeft"` it keeps aligning its left edge to the
 * trigger's left edge in both directions. On a 208px menu under a 108px
 * button that is 100px of drift, which is what the Library's New menu showed:
 * flush against the wrong side of its own trigger in Hebrew.
 *
 * So mirror the token itself. Vertical halves (`top`, `bottom`) are untouched.
 */
export type Placement =
  | 'top' | 'bottom' | 'left' | 'right'
  | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight'
  | 'leftTop' | 'leftBottom' | 'rightTop' | 'rightBottom';

const MIRROR: Record<string, Placement> = {
  left: 'right', right: 'left',
  topLeft: 'topRight', topRight: 'topLeft',
  bottomLeft: 'bottomRight', bottomRight: 'bottomLeft',
  leftTop: 'rightTop', rightTop: 'leftTop',
  leftBottom: 'rightBottom', rightBottom: 'leftBottom',
};

/**
 * The mirror of one token, as a TYPE. Needed because antd narrows placement
 * per component — Dropdown accepts only the top and bottom half — so a function
 * returning the whole union would not typecheck at a Dropdown even though the
 * value it returns is always in range.
 */
type MirrorOf<P extends Placement> =
  P extends 'left' ? 'right' : P extends 'right' ? 'left' :
  P extends 'topLeft' ? 'topRight' : P extends 'topRight' ? 'topLeft' :
  P extends 'bottomLeft' ? 'bottomRight' : P extends 'bottomRight' ? 'bottomLeft' :
  P extends 'leftTop' ? 'rightTop' : P extends 'rightTop' ? 'leftTop' :
  P extends 'leftBottom' ? 'rightBottom' : P extends 'rightBottom' ? 'leftBottom' :
  P;

export function mirrorPlacement<P extends Placement>(
  direction: Direction,
  placement: P,
): P | MirrorOf<P> {
  if (direction !== 'rtl') return placement;
  return (MIRROR[placement] ?? placement) as MirrorOf<P>;
}
